import {SelectDateType} from 'src/types/utils'
import {PropertyType} from 'src/types/property'
import {useEffect, useState} from 'react'
import {GuestSelectType} from 'src/types/guestSelect'
import {useIsAdmin, useIsLoggedIn, useUserInfo} from 'src/hooks/user'
import {useRouter} from 'next/router'
import {useAppContext} from 'src/context/AppProvider'
import {useCurrentLocale} from 'src/hooks/locale'
import {useTranslation} from 'next-i18next'
import {getPropertyName} from 'src/hooks/property'
import * as gtm from 'src/lib/gtm'
import {
  createBookingApi,
  publicCheckBookingAvailableApi,
} from 'src/services/api/booking'
import {dayjs} from 'src/utils/date'
import {sendMessageApi} from 'src/services/api/message'
import {ADMIN_GUEST_MESSAGES_PAGE, ADMIN_INBOX_PAGE} from 'src/constants/route'
import {handleHttpException} from 'src/services/http'
import AppModal from 'src/components/elements/AppModal'
import AppTypography from 'src/components/elements/typography/AppTypography'
import {
  CalendarSection,
  GuestsSection,
} from 'src/components/modules/property/singlePage/sections/Booking'
import MessageBox from 'src/components/modules/booking/sections/Message'
import WarningMessage from 'src/components/elements/helpers/WarningMessage'
import CircularProgress from '@material-ui/core/CircularProgress'
import AppRoundedButton from 'src/components/elements/buttons/AppRoundedButton'

function AskHostRequestBooking(props: {
  initialDates: SelectDateType
  initialGuests: GuestSelectType
  property: PropertyType
  open: boolean
  handleClose: () => void
}) {
  const {property, open, handleClose, initialDates, initialGuests} = props

  const [dates, setDates] = useState<SelectDateType>(initialDates)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<{
    message: string
    booking_uid: string
  } | null>(null)
  const [guests, setGuests] = useState<GuestSelectType>(initialGuests)
  const [message, setMessage] = useState<string>('')

  const isAdmin = useIsAdmin()
  const router = useRouter()
  const {setModal} = useAppContext()
  const currentLocale = useCurrentLocale()
  const {t} = useTranslation('common')
  const userInfo = useUserInfo()
  const isLoggedIn = useIsLoggedIn()
  const propertyName = getPropertyName(property, currentLocale)
  const title = `${t('contact')} ${property.user?.name}`

  const guestCount = guests.adults + guests.children
  const dateRangeSelected = dates[0] && dates[1]

  const bookingIsEnabled = !!(guestCount && dateRangeSelected)

  useEffect(() => {
    setDates(initialDates)
  }, [initialDates])

  useEffect(() => {
    setGuests(initialGuests)
  }, [initialGuests])

  const onBookingRequest = async () => {
    if (loading) return //skip loading

    if (!isLoggedIn) {
      gtm.onBookingCheckoutStarted(userInfo, property, 0)
      setModal('question')
      return
    }

    setError(null)
    setLoading(true)

    try {
      await publicCheckBookingAvailableApi(property.id, {
        start: dayjs(dates[0]).format('YYYY-M-D'),
        end: dayjs(dates[1]).format('YYYY-M-D'),
        options: guests,
        locale: currentLocale,
        user_id: null,
      })

      const booking = await createBookingApi(property.id, {
        start: dayjs(dates[0]).format('YYYY-M-D'),
        end: dayjs(dates[1]).format('YYYY-M-D'),
        options: guests,
        locale: currentLocale,
        first_message: message || t('Hi'),
        is_request_information: true,
        promo_code: null,
        discount: null,
        extra_fees: null,
      })

      const bookingId = booking.id
      await sendMessageApi(message || t('Hi'), bookingId, true)

      gtm.onBookingCheckoutStarted(
        userInfo,
        property,
        booking ? (booking.prices ? booking.prices.total12 : 0) : 0,
      )

      router.push(`${ADMIN_GUEST_MESSAGES_PAGE}?booking_uid=${booking.uid}`)
      handleClose()
      setError(null)
    } catch (err: any) {
      handleHttpException(
        (message) => {
          if (!message) return
          setError({
            message: message,
            booking_uid: err?.response?.data?.booking_uid || '',
          })
        },
        t,
        'booking',
      )(err)
    }

    setLoading(false)
  }

  return (
    <AppModal title={title} open={open} onClose={handleClose}>
      <div className="my-6">
        <div>
          <AppTypography variant="action" neutralColor={900}>
            {t('property')}
          </AppTypography>
          <AppTypography variant="displayXSmallSlim" neutralColor={900}>
            {propertyName}
          </AppTypography>
        </div>

        <CalendarSection
          property={property}
          selectedDates={dates}
          setSelectedDates={setDates}
        />
        <GuestsSection
          property={property}
          guests={guests}
          onChange={setGuests}
        />
        <MessageBox isQuestion message={message} setMessage={setMessage} />
        <WarningMessage
          message={error?.message}
          actionOnClick={() => {
            if (!error?.booking_uid) return
            router.push(
              `${
                isAdmin ? ADMIN_INBOX_PAGE : ADMIN_GUEST_MESSAGES_PAGE
              }?booking_uid=${error.booking_uid}`,
            )
          }}
          actionLabel={
            error?.booking_uid
              ? t('Click here to access it', {ns: 'booking'})
              : undefined
          }
        />
        <br />
        <div className="flex flex-row justify-center items-center mb-2">
          {loading && <CircularProgress size={20} />}
        </div>

        <AppRoundedButton
          fullWidth
          variant="contained"
          color="primary"
          disabled={!bookingIsEnabled || loading}
          onClick={onBookingRequest}
          id="contact_host_send_button"
        >
          {t('Send')}
        </AppRoundedButton>
      </div>
    </AppModal>
  )
}

export default AskHostRequestBooking
